import React, { useEffect, useState, useCallback } from "react";
import { useNotify } from "react-admin";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useTokenOps } from "../../../components/TokenContext";
import { hostname } from "../../../providers/api";

// "Archivo de trabajo" (spec §4.5 / §4.6): separates the working file from the project
// state. The user exports the Excel, fills in the missing data offline, and re-uploads.
// Same endpoints as the old UploadPanel (POST uploadPuente → POST triggerCompleteness →
// GET export); the redesign only changes the LANGUAGE ("Subir Excel actualizado" instead
// of "Subir y recalcular") and adds explicit processing/success feedback.

const useStyles = makeStyles((theme) => ({
  fileName: { fontSize: 15, fontWeight: 700 },
  meta: { fontSize: 12, color: theme.palette.text.secondary, marginBottom: theme.spacing(1.5) },
  hint: { fontSize: 13, color: theme.palette.text.secondary, maxWidth: "64ch", marginBottom: theme.spacing(2) },
  actions: { display: "flex", gap: theme.spacing(1.5), alignItems: "center", flexWrap: "wrap" },
  feedback: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: 8,
    fontSize: 13,
  },
  mode: {
    display: "block",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "6px 12px",
    marginBottom: 8,
    background: theme.palette.grey[50],
  },
  modeDesc: { fontSize: 11.5, color: theme.palette.text.secondary, lineHeight: 1.45, maxWidth: "62ch" },
  fileRow: { display: "flex", gap: theme.spacing(2), alignItems: "flex-end", flexWrap: "wrap", marginBottom: theme.spacing(1) },
  label: { fontSize: 12, color: theme.palette.text.secondary, marginBottom: 4 },
}));

const versionLabel = (v) => {
  let t = v.description
    ? v.description
    : [
        v.lcaMethodologyVersion?.name ? "LCA " + v.lcaMethodologyVersion.name : null,
        v.databaseVersion?.name ? "DB " + v.databaseVersion.name : null,
      ]
        .filter(Boolean)
        .join(" · ");
  if (!t) t = v.id.slice(0, 8);
  return t + (v.isActive ? " (activa)" : "");
};

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
};

// phase: idle | processing | done — drives the §4.6 feedback block.
const WorkFileSection = ({ api, projectId, projectName, lastUpdate, onReload }) => {
  const classes = useStyles();
  const notify = useNotify();
  const tokenOps = useTokenOps();

  const [versions, setVersions] = useState([]);
  const [versionId, setVersionId] = useState("");
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle");
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportLevel, setExportLevel] = useState("");
  const [exportCats, setExportCats] = useState({ missing: true, pending: true, assumed: true });
  const [exportMode, setExportMode] = useState("actual");
  const [exporting, setExporting] = useState(false);

  const loadVersions = useCallback(async () => {
    const r = await api("GET", "/bcome-version?limit=200&page=1&sort=releaseDate,DESC");
    const items = Array.isArray(r.data) ? r.data : r.data?.data ?? [];
    if (!r.ok || !items.length) return setVersions([]);
    setVersions(items);
    setVersionId((prev) => prev || items.find((v) => v.isActive)?.id || items[0].id);
  }, [api]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // "Subir Excel actualizado": upload the Puente file, then chain the async recalc.
  const upload = async () => {
    if (!file) return notify("Selecciona un archivo Excel", "warning");
    if (versions.length && !versionId) return notify("Selecciona una versión de metodología", "warning");

    setPhase("processing");
    const fd = new FormData();
    fd.append("file", file);
    if (versionId) fd.append("bcomeVersionId", versionId);

    const r = await api("POST", `/projects/${projectId}/uploadPuente`, fd, true);
    if (!r.ok) {
      setPhase("idle");
      notify(`Error ${r.status} al subir el archivo`, "error");
      return;
    }
    const rc = await api("POST", `/projects/${projectId}/triggerCompleteness`);
    if (!rc.ok) {
      setPhase("idle");
      notify(`Archivo subido, pero el recálculo devolvió error ${rc.status}`, "warning");
      return;
    }
    // The completeness job is async; refresh now and again shortly to catch the result.
    if (onReload) {
      onReload();
      setTimeout(onReload, 5000);
    }
    setPhase("done");
    setFile(null);
  };

  const exportGaps = async () => {
    setExporting(true);
    const qs = new URLSearchParams();
    if (exportLevel) qs.set("levels", exportLevel);
    if (exportMode && exportMode !== "actual") qs.set("mode", exportMode);
    const cats = [];
    if (exportCats.missing) cats.push("missing");
    if (exportCats.pending) cats.push("pending");
    if (exportCats.assumed) cats.push("assumed");
    if (cats.length === 0) qs.set("cats", "none");
    else if (cats.length < 3) qs.set("cats", cats.join(","));
    const qStr = qs.toString() ? `?${qs.toString()}` : "";

    try {
      const res = await fetch(`${hostname}/projects/${projectId}/export${qStr}`, {
        method: "GET",
        headers: { authorization: "Bearer " + tokenOps.account?.accessToken },
      });
      if (!res.ok) {
        notify(`Error ${res.status} al generar el Excel`, "error");
        return;
      }
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] ?? `gaps-export-${projectId}.xlsx`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      notify(`Descargado: ${filename}`, "success");
    } catch (e) {
      notify(`Error: ${e.message}`, "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box>
      <div className={classes.fileName}>{(projectName || "proyecto").replace(/\s*\([^)]*\)\s*$/, "")}.xlsx</div>
      <div className={classes.meta}>
        Última actualización: {lastUpdate ? fmtDate(lastUpdate) : "—"}
      </div>
      <div className={classes.hint}>
        Completa los datos pendientes en Excel y vuelve a subir el archivo para actualizar el
        proyecto.
      </div>

      <div className={classes.fileRow}>
        <Box>
          <div className={classes.label}>Excel actualizado (.xlsx)</div>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={phase === "processing"}
          />
        </Box>
        {versions.length > 0 && (
          <TextField
            select
            label="Versión de metodología"
            value={versionId}
            onChange={(e) => setVersionId(e.target.value)}
            variant="outlined"
            size="small"
            style={{ minWidth: 240 }}
            disabled={phase === "processing"}
          >
            {versions.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {versionLabel(v)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </div>

      <div className={classes.actions} style={{ alignItems: "flex-start" }}>
        {/* Exportar + su enlace de opciones justo debajo. */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <Button variant="outlined" onClick={exportGaps} disabled={exporting}>
            {exporting ? <CircularProgress size={20} /> : "Exportar Excel"}
          </Button>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setShowExportOptions((s) => !s)}
            style={{ color: "#64748b" }}
          >
            {showExportOptions ? "Ocultar opciones de exportación" : "Opciones de exportación"}
          </Link>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={upload}
          disabled={phase === "processing" || !file}
        >
          {phase === "processing" ? <CircularProgress size={20} color="inherit" /> : "Subir Excel actualizado"}
        </Button>
      </div>

      {/* §4.6 Feedback después de subir */}
      {phase === "processing" && (
        <div className={classes.feedback} style={{ background: "#eff6ff", color: "#1d4ed8" }}>
          <strong>Procesando archivo…</strong>
          <div style={{ marginTop: 2 }}>
            Estamos actualizando los datos y recalculando el estado del proyecto.
          </div>
        </div>
      )}
      {phase === "done" && (
        <div className={classes.feedback} style={{ background: "#ecfdf5", color: "#047857" }}>
          <strong>✓ Archivo actualizado</strong>
        </div>
      )}

      <Collapse in={showExportOptions}>
        <Box mt={2}>
          <Box className={classes.fileRow} mb={2}>
            <TextField
              select
              label="Nivel"
              value={exportLevel}
              onChange={(e) => setExportLevel(e.target.value)}
              variant="outlined"
              size="small"
              style={{ minWidth: 160 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="L1">Solo L1</MenuItem>
              <MenuItem value="L2">Solo L2</MenuItem>
            </TextField>

            <FormControl component="fieldset">
              <FormLabel component="legend" style={{ fontSize: 12 }}>
                Incluir categorías
              </FormLabel>
              <Box display="flex" flexWrap="wrap">
                {[
                  ["missing", "Faltantes"],
                  ["pending", "Asumibles"],
                  ["assumed", "Asumidos"],
                ].map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        size="small"
                        checked={exportCats[key]}
                        onChange={(e) => setExportCats((prev) => ({ ...prev, [key]: e.target.checked }))}
                      />
                    }
                    label={label}
                  />
                ))}
              </Box>
            </FormControl>
          </Box>

          <FormControl component="fieldset">
            <FormLabel component="legend" style={{ fontSize: 12 }}>
              Marcado de asumidos
            </FormLabel>
            <RadioGroup value={exportMode} onChange={(e) => setExportMode(e.target.value)}>
              <FormControlLabel
                className={classes.mode}
                value="actual"
                control={<Radio size="small" />}
                label={
                  <span>
                    <strong>Valores tal cual</strong>
                    <div className={classes.modeDesc}>
                      Cada celda muestra el valor final (real o asumido) sin distinción.
                    </div>
                  </span>
                }
              />
              <FormControlLabel
                className={classes.mode}
                value="marked"
                control={<Radio size="small" />}
                label={
                  <span>
                    <strong>Asumidos marcados — A(valor)</strong>
                    <div className={classes.modeDesc}>
                      Cada celda con default aplicado se envuelve como «A(valor)» para distinguir el
                      dato asumido del real. Sigue siendo re-subible.
                    </div>
                  </span>
                }
              />
              <FormControlLabel
                className={classes.mode}
                value="underscore"
                control={<Radio size="small" />}
                label={
                  <span>
                    <strong>Asumidos marcados — _valor_</strong>
                    <div className={classes.modeDesc}>Igual que el anterior pero con un marcado más ligero, «_valor_».</div>
                  </span>
                }
              />
              <FormControlLabel
                className={classes.mode}
                value="unknown"
                control={<Radio size="small" />}
                label={
                  <span>
                    <strong>Asumidos como «unknown»</strong>
                    <div className={classes.modeDesc}>
                      Las celdas asumidas muestran «unknown». Útil para enviar a proveedores y pedir
                      el dato real: al re-subir, «unknown» se trata como vacío y el hueco se reabre.
                    </div>
                  </span>
                }
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </Collapse>
    </Box>
  );
};

export default WorkFileSection;
